import React, { useEffect, useState, useRef } from 'react';
import PopupBase from "../base/PopupBase";
import CustomButton from "../../components/buttons/CustomButton";
import IconSave from '@mui/icons-material/SaveAltOutlined';
import IconDelete from '@mui/icons-material/Delete';
import ConfirmPopup from '../ConfirmPopup/ConfirmPopup';
import { DataProviderInstance } from "../../api/DataProvider";
import InputField from "../../components/inputfield/InputField";
import { LocalizationProvider, TimeField, TimePicker, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Typography } from '@mui/material';
import dayjs from "dayjs";
import Toast from '../../components/support/Toast';
import ColorPalette from '../../colorpalette/ColorPalette';
import CranialEntryPhotos from './CranialEntryPhotos';
import { clearAttachmentFileCache } from '../../components/patient-attachments/AuthFile';
import { uploadAttachmentItems, releaseUploadItem } from '../PatientAttachmentUploadPopup/uploadAttachments';
import { CRANIAL_PHOTO_CONTEXT, buildCranialPhotoPayload } from '../../pages/appointment/components/cranialAsymmetry/photos/cranialPhotoViews';

const MAX_PHOTOS = 10;

function CreateCranialAsymmetryEntryPopup(props) {

    const { patientId, appointmentId, OnHide, entry, photos, onPhotosChanged } = props;
    const { CompanyConfig, SetPatientCranialAsymmetry, UpdatePatientCranialAsymmetry, DeletePatientCranialAsymmetry, UploadPatientAttachment, DeletePatientAttachment } = DataProviderInstance();
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    const formatDate = (date) => {
        return dayjs(date).set('hour', dayjs().hour()).set('minute', dayjs().minute()).set('second', dayjs().second()).format('YYYY-MM-DD HH:mm:ss');
    }

    const [date, setDate] = useState(entry ? formatDate(dayjs(entry.createdAt)) : formatDate(dayjs()));
    const [apValue, setAPValue] = useState(entry ? entry.ap : "");
    const [bpValue, setBPValue] = useState(entry ? entry.bp : "");
    const [ddValue, setDDValue] = useState(entry ? entry.dd : "");
    const [deValue, setDEValue] = useState(entry ? entry.de : "");

    // Fotos desta medição: as já gravadas (modo editar) e as escolhidas agora.
    const [existingPhotos, setExistingPhotos] = useState(photos || []);
    const [photoItems, setPhotoItems] = useState([]);
    const [photoToRemove, setPhotoToRemove] = useState(null);
    const [sending, setSending] = useState(false);
    const [progress, setProgress] = useState('');
    const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });

    // Guarda o id da medição já gravada, para que um erro no envio das fotos
    // não crie uma segunda medição quando o usuário tentar salvar de novo.
    const savedIdRef = useRef(entry ? entry.id : null);

    const inputAPref = React.createRef(null);
    const inputBPRef = React.createRef(null);
    const inputDDRef = React.createRef(null);
    const inputDERef = React.createRef(null);

    useEffect(() => {
        setExistingPhotos(photos || []);
    }, [photos]);

    const showToast = (message, severity = 'error') => {
        setToast({ open: true, message: message, severity: severity });
    };

    function OnCloseClickHandler() {
        Hide(false);
    }

    function Hide(needsPageReload) {
        photoItems.forEach(releaseUploadItem);
        OnHide(needsPageReload);
    }

    /**
     * Envia as fotos escolhidas com o id da medição já gravada.
     * @returns {Promise<boolean>} true quando não sobrou nada por enviar
     */
    async function uploadPhotos(entryId) {
        if (photoItems.length === 0) return true;

        setSending(true);

        const base = {
            patientId: patientId,
            appointmentId: appointmentId,
            context: CRANIAL_PHOTO_CONTEXT,
            contextId: entryId,
            documentDate: dayjs(date).format('YYYY-MM-DD')
        };

        const result = await uploadAttachmentItems(UploadPatientAttachment, photoItems, base, {
            buildItemPayload: buildCranialPhotoPayload,
            onProgress: (current, total) => setProgress(`Enviando ${current} de ${total}…`)
        });

        setSending(false);
        setProgress('');

        if (result.failed > 0) {
            // A medição continua salva: só as fotos que falharam seguem na tela.
            setPhotoItems(result.failedItems);
            showToast(result.lastError || 'Não foi possível enviar as fotos.');
            return false;
        }

        setPhotoItems([]);

        return true;
    }

    function saveHandler() {
        if (!Validate() || sending) return;

        if (savedIdRef.current) {
            UpdatePatientCranialAsymmetry(
                savedIdRef.current,
                date,
                patientId,
                appointmentId,
                apValue,
                bpValue,
                ddValue,
                deValue,
                (response) => {
                    if (response.status == "SUCCESS") {
                        finishSave(savedIdRef.current);
                    } else {
                        showToast('Não foi possível salvar a medição.');
                    }
                }
            );

            return;
        }

        SetPatientCranialAsymmetry(
            date,
            patientId,
            appointmentId,
            apValue,
            bpValue,
            ddValue,
            deValue,
            (response) => {
                if (response.status == "SUCCESS") {
                    savedIdRef.current = response.data;
                    finishSave(response.data);
                } else {
                    showToast('Não foi possível salvar a medição.');
                }
            }
        );
    }

    async function finishSave(entryId) {
        const uploaded = await uploadPhotos(entryId);

        if (uploaded) Hide(true);
    }

    function deleteHandler() {
        DeletePatientCranialAsymmetry(entry.id, (response) => {
            if (response.status == "SUCCESS") {
                Hide(true);
            }
        });
    }

    function removePhotoHandler() {
        const photo = photoToRemove;

        if (!photo) return;

        DeletePatientAttachment(photo.id, (response) => {
            if (response && response.status === 'SUCCESS') {
                clearAttachmentFileCache(photo.id);
                setExistingPhotos((current) => current.filter(item => item.id !== photo.id));

                if (onPhotosChanged) onPhotosChanged();
            } else {
                showToast('Não foi possível excluir a foto.');
            }

            setPhotoToRemove(null);
        });
    }

    function Validate() {
        return inputAPref.current.Validate() && inputBPRef.current.Validate() && inputDDRef.current.Validate() && inputDERef.current.Validate() && date != null;
    }

    function GetFooter() {
        return (
            <div style={{ display: "flex", gap: "10px" }}>
                {entry && (
                    <CustomButton
                        variant="contained"
                        style="error"
                        label={"Excluir"}
                        icon={<IconDelete />}
                        onClick={() => setShowConfirmDelete(true)}
                        enabled={!sending}
                    />
                )}

                <CustomButton
                    variant="outlined"
                    style="secondary"
                    label={"Cancelar"}
                    onClick={() => Hide(false)}
                    enabled={!sending}
                />

                <CustomButton
                    style="primary"
                    variant="contained"
                    label={entry ? "Atualizar" : "Registrar"}
                    icon={<IconSave />}
                    onClick={saveHandler}
                    loading={sending}
                    enabled={!sending}
                />

                {showConfirmDelete && (
                    <ConfirmPopup
                        Visibility={showConfirmDelete}
                        Title="Confirmar exclusão"
                        Description={existingPhotos.length > 0
                            ? "Tem certeza que deseja excluir esta medida? As fotos desta medição também serão excluídas."
                            : "Tem certeza que deseja excluir esta medida?"}
                        ConfirmLabel="Excluir"
                        OnConfirm={deleteHandler}
                        OnHide={() => setShowConfirmDelete(false)}
                    />
                )}
            </div>
        );
    }


    const handleDateChange = (newValue) => {
        if (newValue != null) {
            const formattedDate = formatDate(newValue)
            setDate(formattedDate);
        }
        else {
            setDate(null);
        }
    }

    function GetContent() {

        return (
            <div style={{ display: "flex", flexDirection: "column", gap: "50px", flexWrap: "wrap", marginTop: "10px" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} >
                    <DatePicker
                        label="Data da medição"
                        defaultValue={dayjs()}
                        format='DD/MM/YYYY'
                        disableFuture={true}
                        onChange={(newValue) => handleDateChange(newValue)}
                    />
                </LocalizationProvider>

                <div style={{ display: "flex", flexDirection: "row", gap: "10px", flexWrap: "wrap" }}>
                    <InputField type={"number"} Mandatory={true} Value={apValue} Label={"AP"} ref={inputAPref} OnUpdateValue={(value) => setAPValue(value)} />
                    <InputField type={"number"} Mandatory={true} Value={bpValue} Label={"BP"} ref={inputBPRef} OnUpdateValue={(value) => setBPValue(value)} />
                </div>
                <div style={{ display: "flex", flexDirection: "row", gap: "10px", flexWrap: "wrap" }}>
                    <InputField type={"number"} Mandatory={true} Value={ddValue} Label={"DD"} ref={inputDDRef} OnUpdateValue={(value) => setDDValue(value)} />
                    <InputField type={"number"} Mandatory={true} Value={deValue} Label={"DE"} ref={inputDERef} OnUpdateValue={(value) => setDEValue(value)} />
                </div>

                <CranialEntryPhotos
                    photos={existingPhotos}
                    items={photoItems}
                    onItemsChange={(items) => setPhotoItems(items)}
                    onRemovePhoto={(photo) => setPhotoToRemove(photo)}
                    disabled={sending}
                    maxFiles={MAX_PHOTOS}
                    onError={(message) => showToast(message, 'warning')}
                />

                {progress && (
                    <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
                        {progress}
                    </Typography>
                )}

                <ConfirmPopup
                    Visibility={!!photoToRemove}
                    Title="Excluir foto"
                    Description="Tem certeza que deseja excluir esta foto? Esta ação não pode ser desfeita."
                    ConfirmLabel="Excluir"
                    OnConfirm={removePhotoHandler}
                    OnHide={() => setPhotoToRemove(null)}
                />

                <Toast
                    open={toast.open}
                    message={toast.message}
                    severity={toast.severity}
                    onClose={() => setToast({ ...toast, open: false })}
                />
            </div>
        )
    }

    return <PopupBase Title={entry ? "Editar medidas" : "Adicionar medidas"} Content={GetContent()} Footer={GetFooter()} OnClose={OnCloseClickHandler} />
}

export default CreateCranialAsymmetryEntryPopup;
